import { useEffect, useRef, useState } from 'react';
import {
  formatShowtime,
  isLateCancel,
  LATE_CANCEL_WINDOW_HOURS,
  movieTitle,
  type BoardScreening,
  type MemberSummary,
} from '@movienight/shared';
import { useTitleOrder } from '../lib/TitleOrderProvider';
import { SeatMapCanvas } from '../seatmap/SeatMapCanvas';
import { SeatList, type SeatListItem } from '../seatmap/SeatList';
import { seatStyle } from '../seatmap/geometry';
import { useSeatMap } from './useSeatMap';
import './seat-sheet.css';

/**
 * Booking, as a sheet from the bottom (DESIGN.md §8).
 *
 * Phone-native on purpose: this is the one screen somebody opens standing up,
 * two minutes after the announcement lands, to grab the good armchair before
 * anyone else does. It is the room's own photograph with markers on it, and
 * tapping a free one is the whole interaction.
 *
 * ## Moving is a tap on somewhere else
 *
 * There is no "change seat" mode. You have one seat; tapping a free one moves
 * you to it, in a single request that never leaves you seatless in between
 * (see `reservations.ts`). Cancelling is a separate, deliberately duller
 * button in the footer, because cancelling is the thing the site remembers.
 */

export function SeatSheet({
  screening,
  me,
  onClose,
}: {
  screening: BoardScreening;
  me: MemberSummary;
  onClose: () => void;
}) {
  const { state, pendingSeatId, problem, take, release } = useSeatMap(screening.id, me);
  /** Pointed at, from the map or from the list — the two light each other. */
  const [hovered, setHovered] = useState<string | null>(null);
  const order = useTitleOrder();
  const closeRef = useRef<HTMLButtonElement>(null);
  const when = formatShowtime(screening.startsAt);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // A slot with no film yet keeps its own name; anything else is the film's,
  // in whichever language this member reads first.
  const title = screening.movie ? movieTitle(screening.movie, order) : 'Glas naroda';

  const map = state.status === 'ready' ? state.map : null;
  const items: SeatListItem[] = (map?.seats ?? []).map((seat, index) => {
    const occupant = map!.taken.find((t) => t.seatId === seat.id);
    const mine = occupant?.user.id === me.id;
    const canTake = !occupant && seat.isBookable && map!.open && pendingSeatId === null;
    return {
      id: seat.id,
      index: index + 1,
      label: seat.label,
      tone: mine ? 'mine' : occupant ? 'taken' : seat.isBookable ? 'free' : 'blocked',
      note: mine
        ? 'ti'
        : occupant
          ? occupant.user.nickname || '…'
          : seat.isBookable
            ? undefined
            : 'ne rezerviše se',
      disabled: !canTake,
    };
  });

  const askThenRelease = () => {
    if (
      isLateCancel(screening.startsAt) &&
      !window.confirm(
        `Manje od ${LATE_CANCEL_WINDOW_HOURS}h do projekcije — ovo se broji. Otkazati?`,
      )
    ) {
      return;
    }
    release();
  };

  return (
    <div className="seat-sheet__scrim" onClick={onClose}>
      <section
        className="seat-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={`Rezervacija mesta — ${title}`}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="seat-sheet__head">
          <div>
            <h2 className="seat-sheet__title">{title}</h2>
            <p className="seat-sheet__where">
              {screening.room.name} · {when.dayMonth} · {when.time}
            </p>
          </div>
          <button ref={closeRef} type="button" className="seat-sheet__close" onClick={onClose}>
            zatvori
          </button>
        </header>

        {state.status === 'loading' && <p className="seat-sheet__note">učitavam mesta…</p>}
        {state.status === 'error' && <p className="seat-sheet__note">{state.message}</p>}

        {state.status === 'ready' && (
          <>
            {problem && (
              <p className="seat-sheet__note seat-sheet__note--bad" role="alert">
                {problem}
              </p>
            )}

            <div className="seatmap-layout">
              <SeatMapCanvas room={state.map.room}>
                {state.map.seats.map((seat, index) => {
                  const occupant = state.map.taken.find((t) => t.seatId === seat.id);
                  const mine = occupant?.user.id === me.id;
                  const free = !occupant && seat.isBookable;
                  const canTake = free && state.map.open && pendingSeatId === null;

                  return (
                    <button
                      key={seat.id}
                      type="button"
                      className={[
                        'seat',
                        occupant ? 'seat--taken' : '',
                        mine ? 'seat--mine' : '',
                        seat.isBookable ? '' : 'seat--blocked',
                        seat.id === hovered ? 'seat--highlight' : '',
                        pendingSeatId === seat.id ? 'seat--pending' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      style={seatStyle(seat)}
                      disabled={!canTake}
                      aria-label={
                        occupant
                          ? `${seat.label}, zauzeto${mine ? ' — tvoje mesto' : ''}`
                          : seat.label
                      }
                      onPointerEnter={() => setHovered(seat.id)}
                      onPointerLeave={() => setHovered(null)}
                      onClick={() => canTake && take(seat.id)}
                    >
                      <span className="seat__facing" />
                      {occupant ? (
                        occupant.user.avatarUrl ? (
                          <img className="seat__avatar" src={occupant.user.avatarUrl} alt="" />
                        ) : (
                          <span className="seat__initial">
                            {/* A booking whose name has not arrived yet: the
                                socket said the seat changed, not who took it. */}
                            {occupant.user.nickname.slice(0, 1).toUpperCase() || '·'}
                          </span>
                        )
                      ) : (
                        <span className="seat__index">{index + 1}</span>
                      )}
                      <span className="seat__label">{seat.label}</span>
                    </button>
                  );
                })}
              </SeatMapCanvas>

              <SeatList
                caption="mesta"
                items={items}
                activeId={hovered}
                onHover={setHovered}
                onPick={(id) => take(id)}
                empty="Ova soba još nema mapu mesta."
              />
            </div>

            <footer className="seat-sheet__foot">
              {state.map.seats.length === 0 ? (
                <p className="seat-sheet__note">Ova soba još nema mapu mesta.</p>
              ) : !state.map.open ? (
                <p className="seat-sheet__note">Rezervacije za ovaj termin su zatvorene.</p>
              ) : state.map.mySeatId ? (
                <>
                  <p className="seat-sheet__yours">
                    Tvoje mesto:{' '}
                    <strong>
                      {state.map.seats.find((s) => s.id === state.map.mySeatId)?.label}
                    </strong>
                    . Izaberi drugo mesto da se premestiš.
                  </p>
                  <button
                    type="button"
                    className="seat-sheet__cancel"
                    onClick={askThenRelease}
                    disabled={pendingSeatId !== null}
                  >
                    otkaži rezervaciju
                  </button>
                </>
              ) : (
                <p className="seat-sheet__yours">Izaberi slobodno mesto.</p>
              )}
            </footer>
          </>
        )}
      </section>
    </div>
  );
}
